import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import TempleCard from '../components/TempleCard';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import DynamicAdBlock from '../components/DynamicAdBlock';
import OptimizedImage from '../components/OptimizedImage';
import { getTemples, getOtherTemples, getSettings, getBlogs } from '../services/api';
import { generateHomeItemListSchema, generateGlobalSchema } from '../utils/schemaGenerator';

const Home = () => {
  const [temples, setTemples] = useState([]);
  const [otherTemples, setOtherTemples] = useState([]);
  const [settings, setSettings] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templeData, otherTempleData, settingsData, blogsData] = await Promise.all([
          getTemples(),
          getOtherTemples(),
          getSettings(),
          getBlogs('published')
        ]);
        setTemples(templeData);
        setOtherTemples(otherTempleData);
        setSettings(settingsData);
        setBlogs(blogsData);
      } catch (error) {
        console.error("Failed to fetch homepage data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredTemples = temples.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOtherTemples = otherTemples.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const homeSchema = generateHomeItemListSchema(temples);
  const globalSchema = generateGlobalSchema();
  const rawSchemas = [...globalSchema, homeSchema].filter(Boolean);

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Nath Nagri Bareilly | 7 Nath Mandir Bareilly | Nathnagri Temples, History & Darshan"
        description="Nath Nagri Bareilly में 7 प्रमुख Nath Mandir की पूरी जानकारी पाएँ – Pashupatinath, Dhopeshwarnath, Bankhandinath आदि। Darshan timing, location, history, events, blogs और local businesses की जानकारी एक ही जगह।"
        keywords="Nath Nagri Bareilly, 7 Nath Mandir Bareilly, Nathnagri temples, Bareilly mandir list, Pashupatinath Mandir Bareilly, Tapeshwarnath Mandir, Bankhandinath Mandir, Bareilly religious places, Bareilly temples timing, Nath temples history, Bareilly tourism, Bareilly darshan time, Shiv mandir Bareilly, temples in Bareilly Uttar Pradesh, Nathnagri Bareilly website"
        schema={rawSchemas}
      />

      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src="/images/HeroBanner_nath-nagri-bareilly-com.png"
            alt="Nath Nagri Bareilly Darshan"
            title="Nath Nagri Bareilly Darshan"
            className="w-full h-full object-cover"
            loading="eager"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1599839619722-39751411ea63?q=80&w=2000&auto=format&fit=crop'; }}
          />
          <div className="absolute inset-0 bg-stone-900/60 mix-blend-multiply"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight drop-shadow-lg"
          >
            <span className="text-saffron-500">नाथ नगरी</span> दर्शन
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-stone-200 mb-10 font-light"
          >
            बरेली के प्रसिद्ध मंदिरों, 7 नाथ मंदिरों, शिव मंदिरों और प्रमुख धार्मिक व पर्यटन स्थलों का अन्वेषण करें, जो इस पवित्र नगरी की आध्यात्मिक पहचान को दर्शाते हैं।
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link
              to="/temples"
              className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-saffron-600 hover:bg-saffron-700 shadow-xl shadow-saffron-600/30 transition-all hover:scale-105"
            >
              मंदिर देखें <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Dynamic Hero Ad */}
      {!loading && <DynamicAdBlock adConfig={settings?.homepageAdsConfig?.hero} />}

      {/* About Us Section */}
      {!loading && settings?.showAboutUs && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b border-stone-200">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="flex-1 space-y-6">
              <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 tracking-tight">नाथ नगरी <span className="text-saffron-600">में आपका स्वागत है</span></h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                बरेली शहर को "नाथ नगरी" के रूप में जाना जाता है क्योंकि इसके चारों कोनों और मध्य में भगवान शिव के सात प्राचीन सिद्धपीठ स्थित हैं।
                ये मंदिर हमारी आध्यात्मिक और सांस्कृतिक विरासत के प्रतीक हैं। हर साल लाखों भक्त यहाँ दर्शन के लिए आते हैं, विशेषकर सावन मास और शिवरात्रि के पावन पर्व पर।
              </p>
              <div>
                <Link to="/about" className="inline-flex items-center text-saffron-600 hover:text-saffron-700 font-bold transition-colors">
                  विस्तार से पढ़ें <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="w-full lg:w-80 flex-shrink-0">
              <AdBanner placement="sidebar" pageName="home" />
            </div>
          </div>
        </section>
      )}

      {/* Dynamic About Ad */}
      {!loading && <DynamicAdBlock adConfig={settings?.homepageAdsConfig?.about} />}

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">

        {/* Banner Ad */}
        <div >
          <AdBanner placement="banner" pageName="home" />
        </div>

        {/* Header & Search */}
        {/* <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 space-y-6 md:space-y-0">
          <div>
            <h2 className="text-3xl font-bold text-stone-800 mb-3 tracking-tight">मंदिर खोजें</h2>
            <p className="text-stone-500 max-w-2xl">अपनी सुविधानुसार बरेली के प्रसिद्ध शिव मंदिरों की जानकारी प्राप्त करें।</p>
          </div>

          <div className="relative max-w-md w-full">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-stone-400" />
            </div>
            <input
              type="text"
              placeholder="मंदिर का नाम खोजें..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-11 pr-4 py-3 bg-white border border-stone-200 rounded-full text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:border-transparent transition-shadow shadow-sm"
            />
          </div>
        </div> */}

        {/* Loading Spinner */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white rounded-2xl h-[400px] border border-stone-100 shadow-sm animate-pulse flex flex-col">
                <div className="h-56 bg-stone-200"></div>
                <div className="p-5 flex-1 space-y-3">
                  <div className="h-6 bg-stone-200 rounded w-1/3"></div>
                  <div className="h-4 bg-stone-200 rounded w-1/4"></div>
                  <div className="space-y-2 mt-4 text-sm text-stone-600">
                    <div className="h-4 bg-stone-200 rounded w-full"></div>
                    <div className="h-4 bg-stone-200 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Nath Temples Section */}
        {!loading && (searchTerm || settings?.showNathTemples !== false) && filteredTemples.length > 0 && (
          <div className="mb-16">
            {!searchTerm && <h3 className="text-2xl font-bold text-stone-800 mb-8 border-b pb-2">प्रमुख नाथ मंदिर</h3>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTemples.map((temple, index) => (
                <TempleCard key={`nath-${temple.id}`} temple={temple} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* Dynamic Nath Temples Ad */}
        {!loading && <DynamicAdBlock adConfig={settings?.homepageAdsConfig?.nathTemples} />}

        {/* Search Empty State */}
        {filteredTemples.length === 0 && filteredOtherTemples.length === 0 && !loading && (
          <div className="text-center py-20 bg-white rounded-3xl border border-stone-100 shadow-sm">
            <h3 className="text-xl text-stone-600">"{searchTerm}" से मेल खाने वाला कोई मंदिर नहीं मिला</h3>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 text-saffron-600 hover:text-saffron-700 font-medium"
            >
              खोज साफ़ करें
            </button>
          </div>
        )}

      </section>

      {/* Other Temples Section */}
      {!loading && (searchTerm || settings?.showOtherTemples) && filteredOtherTemples.length > 0 && (
        <section className="w-full bg-stone-100 py-16 border-t border-stone-200 shadow-inner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-3xl font-bold text-stone-800 mb-8 pb-2">अन्य नाथ नगरी मंदिर (Other NathNagri Temples)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredOtherTemples.slice(0, 3).map((temple, index) => (
                <TempleCard key={`other-${temple.id}`} temple={temple} index={index + temples.length} compact={true} />
              ))}
            </div>
            {filteredOtherTemples.length > 3 && (
              <div className="text-center mt-10">
                <Link to="/temples" className="inline-flex items-center px-6 py-3 text-sm font-bold rounded-xl text-saffron-700 bg-white border border-stone-200 shadow-sm hover:border-saffron-300 hover:shadow-md transition-all">
                  सभी अन्य मंदिर देखें (View All) <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Dynamic Other Temples Ad */}
      {!loading && <DynamicAdBlock adConfig={settings?.homepageAdsConfig?.otherTemples} />}

      {/* Blogs Section */}
      {!loading && settings?.showBlogs && blogs.length > 0 && (
        <section className="bg-white py-16 border-t border-stone-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-10">
              <div>
                <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight">नवीनतम लेख (Blogs)</h2>
                <p className="text-stone-500 mt-2">हमारे समुदाय द्वारा साझा किए गए विचार और जानकारी।</p>
              </div>
              <Link to="/blog" className="text-saffron-600 font-bold hover:text-saffron-700 flex items-center">
                सभी देखें <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.slice(0, 3).map(blog => (
                <Link
                  to={`/blog/${blog.slug || blog.id}`}
                  key={blog.id}
                  className="group flex flex-col justify-between bg-white rounded-2xl p-6 border border-stone-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 hover:border-saffron-300 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-saffron-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  <div>
                    <div className="mb-3 text-xs font-bold text-saffron-600 tracking-wider uppercase flex items-center">
                      <span className="w-2 h-2 rounded-full bg-saffron-500 mr-2"></span>
                      {blog.date} &bull; {blog.author}
                    </div>
                    <h3 className="text-xl font-extrabold text-stone-800 mb-3 group-hover:text-saffron-600 transition-colors leading-snug">
                      {blog.title}
                    </h3>
                    <p className="text-stone-600 line-clamp-3 mb-6 text-sm leading-relaxed">
                      {blog.excerpt || blog.content.replace(/<[^>]+>/g, '')}
                    </p>
                  </div>

                  <div className="mt-auto text-saffron-600 font-bold flex items-center group-hover:underline decoration-saffron-500 decoration-2 underline-offset-4 text-sm">
                    पूरा पढ़ें <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Dynamic Blogs Ad */}
      {!loading && <DynamicAdBlock adConfig={settings?.homepageAdsConfig?.blogs} />}

    </div>
  );
};

export default Home;
